import { Component, EventEmitter, OnInit } from '@angular/core';
import { ExpenditureCategoryService } from '../expenditure-category.service';
import { ExpenditureCategory } from '../expenditure-category';
import { NotificationService } from '../../shared/notification.service';

@Component({
  selector: 'app-expenditure-category-list',
  templateUrl: 'list.component.html',
  styleUrls: [],
})
export class ExpenditureCategoryListComponent implements OnInit {
  public expenditureCategory: ExpenditureCategory;

  /**
   * A list of all the expenditureCategories currently loaded.
   *
   * @type {ExpenditureCategory[]}
   */
  public expenditureCategories: ExpenditureCategory[] = [];

  /**
   * Are we currently performing a loading action?
   *
   * @type {boolean}
   */
  public isLoading: boolean = true;

  /**
   * The total number of pages in this list of expenditureCategories.
   *
   * @type {number}
   */
  public totalPages: number = 1;

  /**
   * The current page of expenditureCategories that we are viewing.
   *
   * @type {number}
   */
  public currentPage: number = 1;

  /**
   * The total number of expenditureCategories that can be viewed in the system.
   *
   * @type {number}
   */
  public totalResults: number = 0;

  /**
   * An Emitter to alert other components when a expenditureCategory has been deleted.
   *
   * @type {EventEmitter<boolean>}
   */
  public expenditureCategoryDeleted: EventEmitter<boolean> = new EventEmitter<
    boolean
  >();

  /**
   * Load up all our required services and subscribe to every observable we need.
   *
   * @param {ExpenditureCategoryService} expenditureCategoryService
   * @param {NotificationService} notificationService
   */
  constructor(
    private expenditureCategoryService: ExpenditureCategoryService,
    private notificationService: NotificationService,
  ) {
    this.expenditureCategoryService.loading.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
    this.expenditureCategoryService.totalPages.subscribe(totalPages => {
      this.totalPages = totalPages;
    });
    this.expenditureCategoryService.currentPage.subscribe(currentPage => {
      this.currentPage = currentPage;
    });
    this.expenditureCategoryService.totalResults.subscribe(totalResults => {
      this.totalResults = totalResults;
    });
  }

  /**
   * When the component is initialised we set it to load
   * page one of the expenditureCategory list.
   */
  public ngOnInit(): void {
    this.loadExpenditureCategories(1);
  }

  /**
   * When we receive a page change from the pagination
   * component we load the appropriate page for the
   * expenditureCategory list.
   *
   * @param {number} pageNumber
   */
  public onPageChanged(pageNumber: number): void {
    this.loadExpenditureCategories(pageNumber);
  }

  /**
   * Loads a list of expenditureCategories using the given per page limit.
   *
   * @param {number} page The current page we require.
   * @param {number} limit The number of results per page.
   */
  public loadExpenditureCategories(page?: number, limit?: number): void {
    const listExpenditureCategories = this.expenditureCategoryService
      .listExpenditureCategories(page, limit)
      .subscribe((expenditureCategories: ExpenditureCategory[]) => {
        this.expenditureCategories = expenditureCategories;
        listExpenditureCategories.unsubscribe();
      });
  }

  /**
   * When receiving confirmation of an item being deleted
   * we call the back end with the appropriate expenditureCategory ID
   * to delete the expenditureCategory.
   *
   * @param {string} id
   */
  public delete(id: string) {
    this.expenditureCategoryService.deleteExpenditureCategory(id).subscribe(
      deleted => {
        this.notificationService.success(
          `ExpenditureCategory Deleted`,
          `ExpenditureCategory with ID ${id} was deleted.`,
          5,
        );
        this.expenditureCategoryDeleted.next(true);
        this.onPageChanged(this.currentPage);
      },
      err => {
        this.notificationService.error(
          `ExpenditureCategory Not Deleted`,
          err.error.message,
          10,
        );
        this.expenditureCategoryDeleted.next(false);
        this.onPageChanged(this.currentPage);
      },
    );
  }
}
